import { type ReactNode } from 'react';
import {
  CONTRACT_PROTECTIONS,
  EXTRACTION_SIGNALS,
  FLEX_LEVELS,
  FLEX_MATCH_OPTIONS,
  FRICTION_PHASES,
  OWNERSHIP_TYPES,
  type AdoptionQuestion,
  type DdwConfig,
  type ScaleOption,
} from '../config';
import {
  adoptionConfidence,
  fmtMoney,
  projectVendorTax,
  truncate,
  valueAtRisk,
  vendorSpend18mo,
} from '../calc';
import type {
  DdwLens,
  DdwScore,
  DdwVendor,
  VendorProjection,
} from '../types';

export type ReportMode = 'single' | 'compare' | 'playbook';

interface Props {
  mode: ReportMode;
  vendorId?: string;
  lens: DdwLens;
  vendors: DdwVendor[];
  config: DdwConfig;
  onBack(): void;
}

/**
 * Reports surface — three modes share a host: per-vendor, side-by-side
 * comparison, and the multi-page master playbook. The master playbook
 * is the artifact buyers take to their decision committee, so the
 * print stylesheet (in ddw.css) is part of the deliverable.
 */
export function Reports({ mode, vendorId, lens, vendors, config, onBack }: Props) {
  const scoredVendors = vendors.filter((v) => v.scored);

  let title: string;
  let body: ReactNode;
  let modeTag: string;
  if (mode === 'single') {
    const v = vendors.find((x) => x.id === vendorId);
    if (!v || !v.score) {
      onBack();
      return null;
    }
    modeTag = 'Per-vendor report';
    title = v.name;
    body = (
      <>
        <PrintHeader />
        <VendorReportPage vendor={v} lens={lens} config={config} />
      </>
    );
  } else if (mode === 'compare') {
    modeTag = 'Side-by-side comparison';
    title = `${scoredVendors.length} vendor${scoredVendors.length === 1 ? '' : 's'}`;
    body = (
      <>
        <PrintHeader />
        <ComparisonPage scoredVendors={scoredVendors} lens={lens} config={config} />
      </>
    );
  } else {
    modeTag = 'Master playbook';
    title = 'Your full evaluation';
    body = (
      <>
        <PrintHeader />
        <PlaybookPages
          scoredVendors={scoredVendors}
          allVendors={vendors}
          lens={lens}
          config={config}
        />
      </>
    );
  }

  const printAction = () => window.print();

  return (
    <div className="ddw-report-wrap">
      <Toolbar tag={modeTag} title={title} onBack={onBack} onPrint={printAction} />
      {body}
      <Toolbar tag={modeTag} title={title} onBack={onBack} onPrint={printAction} />
    </div>
  );
}

function Toolbar({
  tag,
  title,
  onBack,
  onPrint,
}: {
  tag: string;
  title: string;
  onBack(): void;
  onPrint(): void;
}) {
  return (
    <div className="ddw-report-toolbar">
      <div className="ddw-report-toolbar-mode">
        <span className="ddw-report-toolbar-mode-tag">{tag}</span>
        <span className="ddw-report-toolbar-mode-title">{title}</span>
      </div>
      <div className="ddw-report-toolbar-actions">
        <button type="button" className="ddw-tbtn" onClick={onBack}>
          ← Back to vendors
        </button>
        <button type="button" className="ddw-sbtn" onClick={onPrint}>
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
}

function PrintHeader() {
  return (
    <div className="ddw-print-header">
      <div className="ddw-print-header-row">
        <span className="ddw-print-header-brand">m·tr</span>
        <span className="ddw-print-header-url">www.mot-r.com</span>
      </div>
      <div className="ddw-print-header-tagline">
        Due Diligence Workbench · {new Date().toLocaleDateString()}
      </div>
    </div>
  );
}

// ─── Per-vendor page ────────────────────────────────────────

function VendorReportPage({
  vendor,
  lens,
  config,
}: {
  vendor: DdwVendor;
  lens: DdwLens;
  config: DdwConfig;
}) {
  const s = vendor.score!;
  const ownership = OWNERSHIP_TYPES.find((o) => o.key === s.ownership);
  const flexMatch = FLEX_MATCH_OPTIONS.find((f) => f.key === s.flexMatch);
  const scaleOut = config.scaleOut.find((x) => x.key === s.scaleOut);
  const scaleUp = config.scaleUp.find((x) => x.key === s.scaleUp);
  const lensFlex = FLEX_LEVELS.find((f) => f.key === lens.flexibility);
  const protectionsCount = s.protections.filter(Boolean).length;
  const asks = CONTRACT_PROTECTIONS.filter((_, i) => !s.protections[i]);
  const validFits = s.outcomeFit.filter((x): x is number => x != null);
  const outcomeAvg =
    validFits.length > 0 ? (validFits.reduce((a, b) => a + b, 0) / validFits.length).toFixed(1) : '—';
  const proj = projectVendorTax(lens, s, config);
  const baseline = lens.tax.calculatedAnnual;
  const delta = proj.total - baseline;
  const conf = adoptionConfidence(s, config.adoptionQuestions);
  const risk = valueAtRisk(lens, s, config);
  const spend = vendorSpend18mo(lens, s, config);
  const confColor = conf >= 70 ? 'var(--success)' : conf >= 40 ? 'var(--warning)' : 'var(--error)';
  const confTone = conf >= 70 ? '' : 'is-warn';

  return (
    <div className="ddw-report-page">
      <div className="ddw-report-h-tag">Per-vendor report</div>
      <h1 className="ddw-report-h" style={{ fontSize: 26, marginBottom: 4 }}>
        {vendor.name}
      </h1>
      <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 18 }}>
        {vendor.category && `${vendor.category} · `}Scored{' '}
        {vendor.scoredAt ? new Date(vendor.scoredAt).toLocaleDateString() : '—'}
      </div>

      <div className="ddw-vendor-headline-grid">
        <div className="ddw-vendor-headline">
          <div className="ddw-vendor-headline-label">Outcome fit</div>
          <div className="ddw-vendor-headline-value">{outcomeAvg}/5</div>
        </div>
        <div className="ddw-vendor-headline">
          <div className="ddw-vendor-headline-label">Emotional fit</div>
          <div className="ddw-vendor-headline-value">{s.emotionalFit ?? '—'}/5</div>
        </div>
        <div className="ddw-vendor-headline">
          <div className="ddw-vendor-headline-label">Projected adaptation tax</div>
          <div className="ddw-vendor-headline-value">
            {fmtMoney(proj.total)}
            <span style={{ fontSize: 12, color: 'var(--textMuted)', fontWeight: 500 }}>/yr</span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: delta > 0 ? 'var(--warning)' : 'var(--success)',
              marginTop: 2,
            }}
          >
            {delta >= 0 ? '+' : '−'}
            {fmtMoney(Math.abs(delta))} vs baseline
          </div>
        </div>
        <div className="ddw-vendor-headline">
          <div className="ddw-vendor-headline-label">Adoption confidence</div>
          <div className="ddw-vendor-headline-value" style={{ color: confColor }}>
            {conf}%
          </div>
          <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 2 }}>
            {fmtMoney(risk)} at risk
          </div>
        </div>
      </div>

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Outcome fit</div>
      <h2 className="ddw-report-h" style={{ fontSize: 15, marginBottom: 10 }}>
        Average {outcomeAvg}/5
      </h2>
      {lens.outcomes
        .filter((o) => o && o.trim())
        .map((o, i) => {
          const score = s.outcomeFit[i] ?? 0;
          return (
            <div className="ddw-outcome-bar-row" key={i}>
              <div>
                <div className="ddw-outcome-bar-label">
                  {i + 1}. {o}
                </div>
                <div className="ddw-outcome-bar-track">
                  <div className="ddw-outcome-bar-fill" style={{ width: `${(score / 5) * 100}%` }} />
                </div>
              </div>
              <div className="ddw-outcome-bar-score">{score}/5</div>
            </div>
          );
        })}

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Emotional & social fit</div>
      <h2 className="ddw-report-h" style={{ fontSize: 15, marginBottom: 6 }}>
        {s.emotionalFit ?? '—'}/5
      </h2>
      <p className="ddw-report-h-sub">
        Your team wants to feel: <strong>{lens.emotional.feel.join(', ') || '—'}</strong>. Your team
        wants to stop: <strong>{lens.emotional.avoid.join(', ') || '—'}</strong>.
      </p>
      {s.emotionalFit != null && s.emotionalFit <= 2 && (
        <div className="ddw-info-note is-warn">
          <strong>Adoption risk amplified.</strong> Vendor doesn't move team toward the emotional
          outcomes. Functional features won't compensate.
        </div>
      )}

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Vendor ownership</div>
      <h2 className="ddw-report-h" style={{ fontSize: 15 }}>
        {ownership ? ownership.label : '—'}
      </h2>
      <p className="ddw-report-h-sub" style={{ marginTop: 4 }}>
        {ownership?.sub ?? ''}
      </p>
      {ownership && (
        <div className="ddw-report-fact" style={{ border: 'none', paddingTop: 0 }}>
          <div className="ddw-report-fact-label">Typical behaviors to watch for</div>
          <div className="ddw-report-fact-value">{ownership.behaviors}</div>
        </div>
      )}

      <div className="ddw-report-h-tag" style={{ marginTop: 14 }}>
        Extraction signals
      </div>
      <div className="ddw-report-pill-list">
        {EXTRACTION_SIGNALS.map((sig, i) => {
          const val = s.extraction[i];
          if (val == null) {
            return (
              <span key={sig.key} className="ddw-report-pill">
                Unanswered: {sig.label}
              </span>
            );
          }
          const concerning = sig.invert ? !val : val;
          return (
            <span
              key={sig.key}
              className={`ddw-report-pill ${concerning ? 'is-warn' : 'is-yes'}`}
              title={sig.label}
            >
              {val ? 'Yes' : 'No'} — {truncate(sig.label, 60)}
            </span>
          );
        })}
      </div>

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Adaptation capacity</div>
      <Fact label="Flex (configuration)" value={flexMatch?.label ?? '—'} />
      <Fact
        label="Scale-OUT (breadth)"
        value={scaleOut ? `${scaleOut.label} — ${scaleOut.sub}` : '—'}
      />
      <Fact
        label="Scale-UP (depth)"
        value={scaleUp ? `${scaleUp.label} — ${scaleUp.sub}` : '—'}
      />
      <FlexMismatchNote vendorFlex={flexMatch} lensFlex={lensFlex} />

      <div className="ddw-report-h-tag" style={{ marginTop: 14 }}>
        Projected adaptation tax breakdown
      </div>
      <div className="ddw-report-fact">
        <div className="ddw-report-fact-label">Annual total</div>
        <div className="ddw-report-fact-value">
          <strong style={{ color: 'var(--primary)', fontSize: 15 }}>
            {fmtMoney(proj.total)}/yr
          </strong>{' '}
          · vs baseline {fmtMoney(baseline)} → {delta >= 0 ? '+' : '−'}
          {fmtMoney(Math.abs(delta))}
        </div>
      </div>
      <ProjectionBreakdown proj={proj} />

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Contract — confirmed in writing</div>
      <h2 className="ddw-report-h" style={{ fontSize: 15, marginBottom: 10 }}>
        {protectionsCount}/4 confirmed
      </h2>
      <div className="ddw-report-pill-list">
        {CONTRACT_PROTECTIONS.map((p, i) => {
          const c = !!s.protections[i];
          return (
            <span key={p.key} className={`ddw-report-pill ${c ? 'is-yes' : 'is-no'}`}>
              {c ? '✓' : '○'} {p.label}
            </span>
          );
        })}
      </div>
      {asks.length > 0 && (
        <div className="ddw-negotiation-asks">
          <div className="ddw-negotiation-asks-title">Negotiation asks ({asks.length})</div>
          <ul>
            {asks.map((a) => (
              <li key={a.key}>{a.label}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Adoption risk · 12–18 months</div>
      <h2 className="ddw-report-h" style={{ fontSize: 18, color: confColor }}>
        {conf}% adoption confidence
      </h2>
      <div className={`ddw-info-note ${confTone}`}>
        Projected 18-month spend: <strong>{fmtMoney(spend.total)}</strong> (
        {fmtMoney(spend.license18mo)} subscription + {fmtMoney(spend.internalAdaptation18mo)} internal
        adaptation). Value at risk if this fails:{' '}
        <strong style={{ color: 'var(--warning)' }}>{fmtMoney(risk)}</strong>.
        {conf < 70 ? ' Recommended next step: run a premortem before signing.' : ''}
      </div>
      <AdoptionDetail score={s} questions={config.adoptionQuestions} />

      <div className="ddw-report-divider" />

      <div className="ddw-report-h-tag">Gut confidence</div>
      <h2
        className="ddw-report-h"
        style={{
          fontSize: 22,
          color:
            s.gut >= 7 ? 'var(--success)' : s.gut >= 4 ? 'var(--primary)' : 'var(--warning)',
        }}
      >
        {s.gut}/10
      </h2>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="ddw-report-fact">
      <div className="ddw-report-fact-label">{label}</div>
      <div className="ddw-report-fact-value">{value}</div>
    </div>
  );
}

function ProjectionBreakdown({ proj }: { proj: VendorProjection }) {
  return (
    <div
      className="ddw-report-fact"
      style={{ fontSize: 12, color: 'var(--textSecondary)' }}
    >
      Routine config: {fmtMoney(proj.routineCost)} · Scale-out penalty:{' '}
      {fmtMoney(proj.scaleOutPenalty)} · Scale-up penalty: {fmtMoney(proj.scaleUpPenalty)} ·
      Workarounds: {fmtMoney(proj.workaroundCost)} · Major events: {fmtMoney(proj.majorEventCost)}{' '}
      · Productivity drag: {fmtMoney(proj.dragCost)}
    </div>
  );
}

function FlexMismatchNote({
  vendorFlex,
  lensFlex,
}: {
  vendorFlex?: { tier: number; label: string };
  lensFlex?: { key: string; label: string };
}) {
  if (!vendorFlex || !lensFlex) return null;
  let warn = false;
  if (lensFlex.key === 'high' && vendorFlex.tier < 2) warn = true;
  if (lensFlex.key === 'medium' && vendorFlex.tier < 1) warn = true;
  return (
    <div className={`ddw-info-note${warn ? ' is-warn' : ''}`}>
      {warn ? (
        <>
          <strong>Mismatch:</strong> your lens calls for {lensFlex.label.toLowerCase()} flexibility,
          but this vendor's configuration model means routine changes will go through{' '}
          {vendorFlex.label.toLowerCase()}. Expect adaptation tax to climb.
        </>
      ) : (
        <>
          <strong>Match:</strong> this vendor's configuration model fits your{' '}
          {lensFlex.label.toLowerCase()} flexibility requirement.
        </>
      )}
    </div>
  );
}

function AdoptionDetail({
  score,
  questions,
}: {
  score: DdwScore;
  questions: ReadonlyArray<AdoptionQuestion>;
}) {
  return (
    <div style={{ marginTop: 10 }}>
      {questions.map((q) => {
        const ans = q.options.find((o) => o.key === score.adoption[q.key]);
        if (!ans) return null;
        const tone = ans.pts >= 15 ? 'is-yes' : ans.pts >= 8 ? '' : 'is-warn';
        return (
          <div
            key={q.key}
            style={{
              fontSize: 12,
              padding: '5px 0',
              borderBottom: '1px solid var(--borderLight)',
            }}
          >
            <span style={{ color: 'var(--textSecondary)' }}>{q.label}</span>{' '}
            <span className={`ddw-report-pill ${tone}`} style={{ marginLeft: 6 }}>
              {ans.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Comparison page ────────────────────────────────────────

interface CompareCell {
  html: ReactNode;
  klass: string;
}
interface CompareRow {
  label: string;
  cells: CompareCell[];
}

function ComparisonPage({
  scoredVendors,
  lens,
  config,
}: {
  scoredVendors: DdwVendor[];
  lens: DdwLens;
  config: DdwConfig;
}) {
  if (scoredVendors.length === 0) {
    return (
      <div className="ddw-report-page">
        <h2 className="ddw-report-h">No scored vendors yet.</h2>
        <p className="ddw-report-h-sub">Score at least one vendor to view the comparison.</p>
      </div>
    );
  }

  const rows: CompareRow[] = [];

  // Outcome rows
  const filledOutcomes = lens.outcomes.filter((o) => o && o.trim());
  const outcomeMaxes = filledOutcomes.map((_, i) =>
    Math.max(...scoredVendors.map((v) => v.score?.outcomeFit?.[i] ?? 0)),
  );
  filledOutcomes.forEach((outcome, i) => {
    rows.push({
      label: `Outcome ${i + 1}: ${truncate(outcome, 60)}`,
      cells: scoredVendors.map((v) => {
        const score = v.score?.outcomeFit?.[i] ?? 0;
        const isMax = score === outcomeMaxes[i] && score > 0;
        return { html: <strong>{score}/5</strong>, klass: isMax ? 'compare-cell-good' : '' };
      }),
    });
  });

  // Outcome avg
  const avgs = scoredVendors.map((v) => {
    const fits: ReadonlyArray<number | null> = v.score?.outcomeFit ?? [];
    const valid: number[] = fits.filter((x): x is number => x != null);
    return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
  });
  const maxAvg = Math.max(...avgs);
  rows.push({
    label: 'Outcome fit avg',
    cells: avgs.map((a) => ({
      html: <strong>{a.toFixed(1)}/5</strong>,
      klass: a === maxAvg && a > 0 ? 'compare-cell-good' : '',
    })),
  });

  // Emotional
  const emos = scoredVendors.map((v) => v.score?.emotionalFit ?? 0);
  const maxEmo = Math.max(...emos);
  rows.push({
    label: 'Emotional & social fit',
    cells: emos.map((e) => ({
      html: <strong>{e}/5</strong>,
      klass: e === maxEmo && e > 0 ? 'compare-cell-good' : e <= 2 ? 'compare-cell-warn' : '',
    })),
  });

  rows.push({
    label: 'Ownership',
    cells: scoredVendors.map((v) => {
      const o = OWNERSHIP_TYPES.find((x) => x.key === v.score?.ownership);
      return { html: o ? o.short : '—', klass: '' };
    }),
  });

  EXTRACTION_SIGNALS.forEach((sig, i) => {
    rows.push({
      label: truncate(sig.label, 60),
      cells: scoredVendors.map((v) => {
        const val = v.score?.extraction[i];
        if (val == null) return { html: '—', klass: '' };
        const concerning = sig.invert ? !val : val;
        return {
          html: val ? 'Yes' : 'No',
          klass: concerning ? 'compare-cell-warn' : 'compare-cell-good',
        };
      }),
    });
  });

  rows.push({
    label: 'Flex (config)',
    cells: scoredVendors.map((v) => {
      const fm = FLEX_MATCH_OPTIONS.find((x) => x.key === v.score?.flexMatch);
      const lf = FLEX_LEVELS.find((f) => f.key === lens.flexibility);
      let klass = '';
      if (fm && lf) {
        let warn = false;
        if (lf.key === 'high' && fm.tier < 2) warn = true;
        if (lf.key === 'medium' && fm.tier < 1) warn = true;
        klass = warn ? 'compare-cell-warn' : 'compare-cell-good';
      }
      return { html: fm ? fm.label.split(' (')[0] : '—', klass };
    }),
  });

  rows.push({
    label: 'Scale-OUT (breadth)',
    cells: scoredVendors.map((v) => {
      const so = config.scaleOut.find((x) => x.key === v.score?.scaleOut) as ScaleOption | undefined;
      if (!so) return { html: '—', klass: '' };
      const klass = so.penaltyMult === 0 ? 'compare-cell-good' : so.penaltyMult >= 1 ? 'compare-cell-warn' : '';
      return { html: so.label, klass };
    }),
  });

  rows.push({
    label: 'Scale-UP (depth)',
    cells: scoredVendors.map((v) => {
      const su = config.scaleUp.find((x) => x.key === v.score?.scaleUp) as ScaleOption | undefined;
      if (!su) return { html: '—', klass: '' };
      const klass = su.penaltyMult === 0 ? 'compare-cell-good' : su.penaltyMult >= 1 ? 'compare-cell-warn' : '';
      return { html: su.label, klass };
    }),
  });

  const projs = scoredVendors.map((v) => (v.score ? projectVendorTax(lens, v.score, config).total : 0));
  const minProj = Math.min(...projs);
  const maxProj = Math.max(...projs);
  rows.push({
    label: 'Projected adaptation tax',
    cells: projs.map((p) => ({
      html: <strong>{fmtMoney(p)}/yr</strong>,
      klass:
        p === minProj && projs.length > 1
          ? 'compare-cell-good'
          : p === maxProj && projs.length > 1
            ? 'compare-cell-warn'
            : '',
    })),
  });

  const protCounts = scoredVendors.map((v) => v.score?.protections.filter(Boolean).length ?? 0);
  const maxProt = Math.max(...protCounts);
  rows.push({
    label: 'Contract — confirmed',
    cells: protCounts.map((c) => ({
      html: <strong>{c}/4</strong>,
      klass: c === maxProt && c > 0 ? 'compare-cell-good' : '',
    })),
  });

  const confs = scoredVendors.map((v) => adoptionConfidence(v.score, config.adoptionQuestions));
  const maxConf = Math.max(...confs);
  rows.push({
    label: 'Adoption confidence',
    cells: confs.map((c) => ({
      html: <strong>{c}%</strong>,
      klass:
        c === maxConf && c > 0 ? 'compare-cell-good' : c < 40 ? 'compare-cell-warn' : '',
    })),
  });

  const risks = scoredVendors.map((v) => (v.score ? valueAtRisk(lens, v.score, config) : 0));
  const minRisk = Math.min(...risks);
  const maxRisk = Math.max(...risks);
  rows.push({
    label: 'Value at risk (18mo)',
    cells: risks.map((r) => ({
      html: <strong>{fmtMoney(r)}</strong>,
      klass:
        r === minRisk && risks.length > 1
          ? 'compare-cell-good'
          : r === maxRisk && risks.length > 1
            ? 'compare-cell-warn'
            : '',
    })),
  });

  const guts = scoredVendors.map((v) => v.score?.gut ?? 0);
  const maxGut = Math.max(...guts);
  rows.push({
    label: 'Gut confidence',
    cells: guts.map((g) => ({
      html: <strong>{g}/10</strong>,
      klass: g === maxGut && g > 0 ? 'compare-cell-good' : g <= 3 ? 'compare-cell-warn' : '',
    })),
  });

  return (
    <div className="ddw-report-page">
      <div className="ddw-report-h-tag">Side-by-side comparison</div>
      <h1 className="ddw-report-h" style={{ fontSize: 24 }}>
        {scoredVendors.length} vendor{scoredVendors.length === 1 ? '' : 's'} compared against your
        lens.
      </h1>
      <p className="ddw-report-h-sub">
        Teal cells indicate the strongest fit per row. Amber cells flag concerning signals or known
        mismatches. Use this to identify where each vendor genuinely stands out — and where they
        don't.
      </p>

      <div className="ddw-compare-wrap" style={{ marginTop: 18 }}>
        <table className="ddw-compare-table">
          <thead>
            <tr>
              <th>Criterion</th>
              {scoredVendors.map((v) => (
                <th key={v.id} className="vendor-col">
                  {v.name}
                  {v.category && <span className="ddw-compare-cat">{v.category}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="row-label">{r.label}</td>
                {r.cells.map((c, j) => (
                  <td key={j} className={c.klass}>
                    {c.html}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Master playbook ────────────────────────────────────────

function PlaybookPages({
  scoredVendors,
  allVendors,
  lens,
  config,
}: {
  scoredVendors: DdwVendor[];
  allVendors: DdwVendor[];
  lens: DdwLens;
  config: DdwConfig;
}) {
  const fr = FRICTION_PHASES.find((f) => f.key === lens.frictionPhase);
  const fl = FLEX_LEVELS.find((f) => f.key === lens.flexibility);
  const filledOutcomes = lens.outcomes.filter((o) => o && o.trim());

  let highestConf: DdwVendor | null = null;
  let lowestRisk: DdwVendor | null = null;
  let lowestTax: DdwVendor | null = null;
  let highestEmo: DdwVendor | null = null;

  if (scoredVendors.length > 0) {
    highestConf = scoredVendors.reduce((a, b) =>
      adoptionConfidence(a.score, config.adoptionQuestions) >=
      adoptionConfidence(b.score, config.adoptionQuestions)
        ? a
        : b,
    );
    lowestRisk = scoredVendors.reduce((a, b) =>
      valueAtRisk(lens, a.score!, config) <= valueAtRisk(lens, b.score!, config) ? a : b,
    );
    lowestTax = scoredVendors.reduce((a, b) =>
      projectVendorTax(lens, a.score!, config).total <=
      projectVendorTax(lens, b.score!, config).total
        ? a
        : b,
    );
    highestEmo = scoredVendors.reduce((a, b) =>
      (a.score?.emotionalFit ?? 0) >= (b.score?.emotionalFit ?? 0) ? a : b,
    );
  }

  return (
    <>
      <div className="ddw-report-page">
        <div className="ddw-report-cover-tag">
          Due Diligence Workbench · Master playbook
        </div>
        <div className="ddw-report-cover-title">{lens.jobStatement || 'Your evaluation'}</div>
        <div className="ddw-report-cover-sub">
          Generated {new Date().toLocaleDateString()} · {scoredVendors.length} of{' '}
          {allVendors.length} vendors scored
        </div>

        <div className="ddw-report-cover-stat-grid">
          <CoverStat label="Friction phase" value={fr?.label ?? '—'} />
          <CoverStat label="Flexibility requirement" value={fl?.label ?? '—'} />
          <CoverStat
            label="Baseline adaptation tax"
            value={
              <>
                {fmtMoney(lens.tax.calculatedAnnual)}
                <span style={{ fontSize: 12, color: 'var(--textMuted)', fontWeight: 500 }}>/yr</span>
              </>
            }
          />
          <CoverStat label="Vendors evaluated" value={String(allVendors.length)} />
        </div>

        {scoredVendors.length > 0 && (
          <>
            <div className="ddw-report-h-tag" style={{ marginTop: 18 }}>
              Headline findings
            </div>
            <div className="ddw-report-cover-stat-grid">
              <CoverStat
                label="Highest adoption confidence"
                value={
                  <span style={{ fontSize: 16 }}>
                    {highestConf!.name}
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--success)',
                        fontWeight: 600,
                        marginLeft: 6,
                      }}
                    >
                      {adoptionConfidence(highestConf!.score, config.adoptionQuestions)}%
                    </span>
                  </span>
                }
              />
              <CoverStat
                label="Lowest projected adaptation tax"
                value={
                  <span style={{ fontSize: 16 }}>
                    {lowestTax!.name}
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--success)',
                        fontWeight: 600,
                        marginLeft: 6,
                      }}
                    >
                      {fmtMoney(projectVendorTax(lens, lowestTax!.score!, config).total)}/yr
                    </span>
                  </span>
                }
              />
              <CoverStat
                label="Best emotional fit"
                value={
                  <span style={{ fontSize: 16 }}>
                    {highestEmo!.name}
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--success)',
                        fontWeight: 600,
                        marginLeft: 6,
                      }}
                    >
                      {highestEmo!.score?.emotionalFit ?? '—'}/5
                    </span>
                  </span>
                }
              />
              <CoverStat
                label="Lowest value at risk"
                value={
                  <span style={{ fontSize: 16 }}>
                    {lowestRisk!.name}
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--success)',
                        fontWeight: 600,
                        marginLeft: 6,
                      }}
                    >
                      {fmtMoney(valueAtRisk(lens, lowestRisk!.score!, config))}
                    </span>
                  </span>
                }
              />
            </div>
          </>
        )}

        <div className="ddw-report-h-tag" style={{ marginTop: 18 }}>
          Outcomes you're evaluating against
        </div>
        <div>
          {filledOutcomes.map((o, i) => (
            <Fact key={i} label={`Outcome ${i + 1}`} value={o} />
          ))}
        </div>

        <div className="ddw-report-h-tag" style={{ marginTop: 18 }}>
          How success should feel
        </div>
        <div className="ddw-report-pill-list">
          {lens.emotional.feel.map((f) => (
            <span key={f} className="ddw-report-pill is-yes">
              {f}
            </span>
          ))}
        </div>
        <div className="ddw-report-h-tag" style={{ marginTop: 12 }}>
          …and what should stop
        </div>
        <div className="ddw-report-pill-list">
          {lens.emotional.avoid.map((a) => (
            <span key={a} className="ddw-report-pill is-warn">
              {a}
            </span>
          ))}
        </div>

        <div className="ddw-report-cover-meta">
          This playbook is your evaluation artifact — share with your decision team.
        </div>
      </div>

      {scoredVendors.map((v) => (
        <VendorReportPage key={v.id} vendor={v} lens={lens} config={config} />
      ))}

      {scoredVendors.length >= 2 && (
        <ComparisonPage scoredVendors={scoredVendors} lens={lens} config={config} />
      )}

      <div className="ddw-report-page">
        <div className="ddw-report-h-tag">Recommended next step</div>
        <h2 className="ddw-report-h">Run a premortem before you sign.</h2>
        <p className="ddw-report-h-sub">
          Even after thorough evaluation, the strongest single predictor of a successful
          implementation is whether you've imagined how it could fail — and built mitigations
          before you need them. Klein's premortem technique improves risk identification by ~30%.
        </p>
        <div className="ddw-premortem-cta">
          <h3>Premortem facilitation guide</h3>
          <p>
            A 60–90 minute session with 6–12 people. Includes a facilitator script, legal-tech-specific
            failure prompts, and an action planning template.
          </p>
          <p style={{ fontSize: 12, color: 'var(--textMuted)' }}>
            Available as a free PDF download — coming soon.
          </p>
        </div>
      </div>
    </>
  );
}

function CoverStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="ddw-report-cover-stat">
      <div className="ddw-report-cover-stat-label">{label}</div>
      <div className="ddw-report-cover-stat-value">{value}</div>
    </div>
  );
}
